import { useTranslation } from 'react-i18next';
import type { ConsentStatusItem } from '@/api/consents/consentsApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { ModalOverlay } from '@/components/ui/modalOverlay';

interface WithdrawConsentDialogProps {
  consent: ConsentStatusItem;
  isSubmitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function WithdrawConsentDialog({
  consent,
  isSubmitting,
  onConfirm,
  onCancel,
}: WithdrawConsentDialogProps) {
  const { t } = useTranslation(['clients', 'common']);

  return (
    <ModalOverlay onRequestClose={onCancel} variant="sheet">
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto bg-zoowhite border border-zooborder rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:pb-6">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-zoowarningbg">
          <Icon name="AlertTriangle" className="text-xl text-zoowarningtext" />
        </div>
        <h2 className="text-lg font-bold text-zooindigo">
          {t('clients:consents.withdrawDialog.title', { title: consent.title })}
        </h2>
        <p className="text-sm text-zoomuted mt-1.5">
          {consent.isRequired === true
            ? t('clients:consents.withdrawDialog.requiredHint')
            : t('clients:consents.withdrawDialog.optionalHint')}
        </p>
        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            isLoading={isSubmitting}
            onClick={onConfirm}
          >
            {t('clients:consents.withdrawDialog.confirm')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
